import { Check, CloudOff, History, Loader2, Trash2, TriangleAlert } from 'lucide-react';
import type { DraftSaveState } from './useKitDraftSync';

interface KitDraftBannerProps {
  /** Display name on the loaded draft, or null when this is a fresh start. */
  pickedUpFrom: string | null;
  pickedUpAt: string | null;
  /** True when the loaded draft is the current user's own earlier work. */
  isOwn: boolean;
  saveState: DraftSaveState;
  conflictMessage: string | null;
  onReload: () => void;
  onDiscard: () => void;
  isDiscarding: boolean;
}

function timeOnly(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** The autosave indicator: quiet until it has something to report. */
function SaveIndicator({ state }: { state: DraftSaveState }) {
  if (state === 'saving') {
    return (
      <span className="inline-flex items-center gap-1 text-fg-dim">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Saving
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="inline-flex items-center gap-1 text-fg-muted">
        <Check className="h-3.5 w-3.5" aria-hidden />
        Progress saved
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className="inline-flex items-center gap-1 text-warning-soft-fg">
        <CloudOff className="h-3.5 w-3.5" aria-hidden />
        Not saved
      </span>
    );
  }
  return null;
}

/**
 * Sits above the item list and answers the two questions a desk picking up
 * someone else's work has: where did this come from, and is what I'm typing
 * being kept.
 */
export function KitDraftBanner({
  pickedUpFrom,
  pickedUpAt,
  isOwn,
  saveState,
  conflictMessage,
  onReload,
  onDiscard,
  isDiscarding,
}: KitDraftBannerProps) {
  const resumed = pickedUpFrom !== null || pickedUpAt !== null;

  if (conflictMessage) {
    return (
      <div className="mb-4 rounded-token-sm bg-warning-soft/15 px-3 py-2.5 text-sm text-warning-soft-fg">
        <div className="flex items-start gap-2">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0">
            <p className="font-medium">{conflictMessage}</p>
            <p className="mt-0.5 text-xs opacity-90">
              Your edits are still on screen but are no longer being saved. Loading their version
              replaces what you see here.
            </p>
            <button
              type="button"
              onClick={onReload}
              className="mt-2 rounded-token-sm bg-warning-soft/25 px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-warning-soft/40"
            >
              Load their version
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!resumed) {
    // Nothing was picked up, so the only thing worth saying is whether the
    // desk's typing is safe. Suppressed entirely until the first save.
    if (saveState === 'idle') return null;
    return (
      <div className="mb-4 flex justify-end text-xs">
        <SaveIndicator state={saveState} />
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-token-sm bg-info-soft/15 px-3 py-2.5 text-sm text-info-soft-fg">
      <div className="flex items-start gap-2">
        <History className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="font-medium">
            {isOwn ? 'Picked up your unfinished kit' : `Picked up from ${pickedUpFrom ?? 'another desk'}`}
            {pickedUpAt ? `, ${timeOnly(pickedUpAt)}` : ''}
          </p>
          <p className="mt-0.5 text-xs opacity-90">
            Ticks, serials and notes were restored. Condition photos are not kept in a draft, so
            any that were taken need retaking before signing.
          </p>
          <div className="mt-2 flex items-center gap-3 text-xs">
            <button
              type="button"
              onClick={onDiscard}
              disabled={isDiscarding}
              className="inline-flex items-center gap-1 rounded-token-sm bg-info-soft/25 px-2.5 py-1 font-semibold transition-colors hover:bg-info-soft/40 disabled:opacity-60"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              {isDiscarding ? 'Discarding' : 'Start again'}
            </button>
            <SaveIndicator state={saveState} />
          </div>
        </div>
      </div>
    </div>
  );
}
